/**
 * render_display_component.jsx  (重構版)
 *
 * 職責：依 element.type 路由到對應的子元件，並準備共用資料（options、chartData）。
 * 所有 element type 的實作已拆分至 render_display_elements/ 子目錄。
 *
 * 拆分對照：
 *   display_input_elements.jsx     → title, text-content, number_input, email_input,
 *                                    phone_input, url_input, password_input,
 *                                    textarea, textarea_ocr, textarea_vernacular,
 *                                    datetime, datetime_picker
 *   display_selection_elements.jsx → dropdown, multi_select_list, radio_button,
 *                                    checkbox, switch, color_picker
 *   display_media_elements.jsx     → photo, qrcode, location, slider, star_rating,
 *                                    upload_image, upload_file, signature,
 *                                    bar_chart, pie_chart, line_chart, data_table
 *   (flow / canvas 類型保留在此檔，因耦合 canvas state 較深)
 */

import React, { useState } from "react"
import { Button } from "../ui/button"
import { Badge, ImageFileId, LucideIcon, DataTable, getValueByCode, UploadImg, Signature, UploadFile, Input } from "../ui/ot"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card"
import { Trash2, Plus, User, CalendarIcon, Clock, FileText, Link, GripVertical } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog"
import { getElementPosition, transformResponses } from "./base_canvas_element_utils"

import {
    TitleElement, TextContentElement, NumberInputElement,
    EmailInputElement, PhoneInputElement, UrlInputElement, PasswordInputElement,
    TextareaElement, TextareaOcrElement, TextareaVernacularElement,
    DatetimeElement, DatetimePickerElement,
} from "./render_display_elements/display_input_elements"

import {
    DropdownElement, MultiSelectListElement, RadioButtonElement,
    CheckboxElement, SwitchElement, ColorPickerElement,
} from "./render_display_elements/display_selection_elements"

import {
    PhotoElement, QRCodeElement, LocationElement, SliderElement, StarRatingElement,
    UploadImageElement, UploadFileElement, SignatureElement,
    BarChartElement, PieChartElement, LineChartElement, DataTableElement,
} from "./render_display_elements/display_media_elements"

const RenderDisplayComponent = ({
    element,
    selectedElement,
    selectedType,
    connectionMode,
    connectionState,
    setSelectedElement,
    setPages,
    addChildNode,
    handleDeleteSelectedElement,
    startConnection,
    finishConnection,
    cancelConnection,
    createConnectedNode,
    canvasMode,
    getPriorityColor,
    getPriorityText,
    getLineStyle,
    getEdgeCenterGeom,
    computeLineGeom,
    handleElementClick,
    currentPageId,
    setConnectionMode,
    currentPage,
    getCardChildren = () => { },
    onDropElement,
    CARD_INNER_PADDING = 16,
    mode,
    isEditing,
    setEditingElementId,
    handleMouseDown,
    setEditingContent,
    RenderFillComponent,
    updateElementContent,
    editingContent,
    handleFinishEditing,
    handleResizeMouseDown,
    showBlueBorder,
    handleMouseMove,
    handleResizeMouseMove,
    handleMouseUp,
    handleResizeMouseUp,
    getPageCards = () => [],
    handleChangeContainer = () => { },
    getOrFetchItem = () => { },
    getItem = () => { },
}) => {
    const [uploadedFiles, setUploadedFiles] = useState({})
    const [fileViewerOpen, setFileViewerOpen] = useState(false)
    const [viewingFile, setViewingFile] = useState(null)
    const [formData, setFormData] = useState({})

    // ── 共用資料準備 ──────────────────────────────────────────────────────────
    const form_form_column_data = element?.form_form_column_data
    const form_form_column_data_first = form_form_column_data?.find((_) => true)

    // 靜態選項
    const staticOptions = (form_form_column_data_first?.form_column_selection_option_data || []).map(opt => {
        const cst = (opt?.selection_option_classify_structure_type_data || []).find(_ => true)
        const tw = (cst?.name_language_data || []).find(n => n.language_culture_code === "zh-tw")
        return { label: tw?.language_culture_value ?? "", value: opt?.selection_option_id }
    })

    // 動態選項（來源文件）
    const form_column_source_data_first = (form_form_column_data_first?.form_column_source_data || []).find(_ => true)
    const document_source_data = (form_column_source_data_first?.document_source_data || [])
    const response_data = (document_source_data.find(_ => true)?.response_data || [])
    const dynamicOptions = response_data.map(item => ({
        label: getValueByCode({ code: "名稱", response_form_value_data: item?.response_form_value_data }),
        value: item?.response_id,
    }))
    const finalOptions = [...staticOptions, ...dynamicOptions]

    // 圖表資料
    let chartData = []
    document_source_data.forEach(item => {
        chartData = transformResponses(
            item?.response_data || [],
            form_form_column_data_first?.default_value?.columns || []
        ) || []
    })

    // fill/fill_disable 模式 flag（給子元件做 layout 判斷）
    const isFillMode = mode === "fill" || mode === "fill_disable"

    // ── 共用 props 包 ─────────────────────────────────────────────────────────
    const commonProps = {
        element,
        form_form_column_data_first,
        finalOptions,
        chartData,
        isFillMode,
        mode,
    }

    // ── 路由 ─────────────────────────────────────────────────────────────────
    const type = element?.type

    switch (type) {

        // ── 文字 / 輸入類 ────────────────────────────────────────────────────
        case "title":
            return (
                <TitleElement
                    {...commonProps}
                    isEditing={isEditing}
                    selectedElement={selectedElement}
                    editingContent={editingContent}
                    setEditingContent={setEditingContent}
                    handleFinishEditing={handleFinishEditing}
                    setEditingElementId={setEditingElementId}
                />
            )

        case "text-content": return <TextContentElement   {...commonProps} />
        case "number_input": return <NumberInputElement   {...commonProps} />
        case "email_input": return <EmailInputElement    {...commonProps} />
        case "phone_input": return <PhoneInputElement    {...commonProps} />
        case "url_input": return <UrlInputElement      {...commonProps} />
        case "password_input": return <PasswordInputElement {...commonProps} />
        case "textarea": return <TextareaElement      {...commonProps} />
        case "textarea_ocr": return <TextareaOcrElement   {...commonProps} />
        case "textarea_vernacular": return <TextareaVernacularElement {...commonProps} />
        case "datetime": return <DatetimeElement      {...commonProps} />
        case "datetime_picker": return <DatetimePickerElement {...commonProps} />
        case "span": {
            const value = element?.content ?? form_form_column_data_first?.default_value ?? "";
            const label = form_form_column_data_first?.alt ?? form_form_column_data_first?.code;
            {
                console.log({
                    element
                })
            }
            return (
                <div className="flex  w-full h-full pointer-events-auto"
                    style={{
                        justifyContent: element?.style?.justifyContent || "center",
                    }}>

                    <div className="  max-w-md">
                        <div
                        >
                            {/* Label */}
                            {/* {label && (
                                <Label className="block text-gray-700 font-medium mb-2">
                                    {label}
                                </Label>
                            )} */}

                            {/* 編輯模式 */}
                            {isEditing && selectedElement?.id === element?.id ? (
                                <Input
                                    autoFocus
                                    className="w-full mb-2"
                                    value={editingContent ?? value}
                                    onChange={(e) => setEditingContent(e.target.value)}
                                    onBlur={() => handleFinishEditing()}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleFinishEditing();
                                        }
                                    }}
                                />
                            ) : (
                                /* 顯示模式 */
                                <div
                                    style={{
                                        textColor: element?.style?.textColor || "#000",
                                        fontSize: element?.style?.fontSize || 14,
                                        fontFamily: element?.style?.fontFamily || "inherit",
                                        letterSpacing
                                            : element?.style?.letterSpacing ? `${element.style.letterSpacing}px` : "normal",
                                    }}
                                    onDoubleClick={() => {
                                        if (mode === "design") {
                                            setEditingElementId(element?.id);
                                            setEditingContent(value);
                                        }
                                    }}
                                >
                                    {value || "文字描述"}
                                </div>
                            )}

                            {/* 說明 */}
                            {form_form_column_data_first?.help_text && (
                                <p className="text-gray-500 text-xs">
                                    {form_form_column_data_first?.help_text}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            );
        }
        case "hashtag": {
            const value = element?.content ?? form_form_column_data_first?.default_value ?? "";
            const label = form_form_column_data_first?.alt ?? form_form_column_data_first?.code;
            {
                console.log({
                    element
                })
            }
            return (
                <div className="flex  w-full h-full pointer-events-auto"
                    style={{
                        justifyContent: element?.style?.justifyContent || "center",
                    }}>

                    <div className="  max-w-md">
                        <div
                        >
                            {/* Label */}
                            {/* {label && (
                                <Label className="block text-gray-700 font-medium mb-2">
                                    {label}
                                </Label>
                            )} */}

                            {/* 編輯模式 */}
                            {isEditing && selectedElement?.id === element?.id ? (
                                <Input
                                    autoFocus
                                    className="w-full mb-2"
                                    value={editingContent ?? value}
                                    onChange={(e) => setEditingContent(e.target.value)}
                                    onBlur={() => handleFinishEditing()}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleFinishEditing();
                                        }
                                    }}
                                />
                            ) : (
                                /* 顯示模式 */
                                <Badge
                                    style={{
                                        textColor: element?.style?.textColor || "#000",
                                        fontSize: element?.style?.fontSize || 14,
                                        fontFamily: element?.style?.fontFamily || "inherit",
                                        letterSpacing
                                            : element?.style?.letterSpacing ? `${element.style.letterSpacing}px` : "normal",
                                    }}
                                    onDoubleClick={() => {
                                        if (mode === "design") {
                                            setEditingElementId(element?.id);
                                            setEditingContent(value);
                                        }
                                    }}
                                    className={`text-xs ${getPriorityColor(element?.priority)}`}
                                >
                                    {value || "文字描述"}
                                </Badge>
                            )}

                            {/* 說明 */}
                            {form_form_column_data_first?.help_text && (
                                <p className="text-gray-500 text-xs">
                                    {form_form_column_data_first?.help_text}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            );
        }

        // ── 選擇類 ──────────────────────────────────────────────────────────
        case "dropdown": return <DropdownElement       {...commonProps} />
        case "multi_select_list": return <MultiSelectListElement {...commonProps} />
        case "radio_button": return <RadioButtonElement    {...commonProps} />
        case "checkbox": return <CheckboxElement       {...commonProps} />
        case "switch": return <SwitchElement         {...commonProps} />
        case "color_picker": return <ColorPickerElement    {...commonProps} />

        // ── 媒體 / 圖表類 ────────────────────────────────────────────────────
        case "photo": return <PhotoElement        {...commonProps} />
        case "image": return <PhotoElement        {...commonProps} />
        case "qrcode": return <QRCodeElement       {...commonProps} />
        case "location": return <LocationElement     {...commonProps} />
        case "slider": return <SliderElement       {...commonProps} />
        case "star_rating": return <StarRatingElement   {...commonProps} />
        case "upload_img": return <UploadImageElement  {...commonProps} />
        case "upload_file": return <UploadFileElement   {...commonProps} />
        case "signature": return <SignatureElement    {...commonProps} />
        case "bar_chart": return <BarChartElement     {...commonProps} />
        case "pie_chart": return <PieChartElement     {...commonProps} />
        case "line_chart": return <LineChartElement    {...commonProps} />
        case "table_chart": return <DataTableElement    {...commonProps} />

        // ── 流程節點（保留原邏輯，耦合 canvas state）───────────────────────
        case "start_node":
        case "end_node":
        case "process_node":
        case "decision_node": {
            const iconName = {
                start_node: "Play", end_node: "Square",
                process_node: "Circle", decision_node: "Diamond",
            }
            const bgClass = {
                start_node: "bg-green-100", end_node: "bg-red-100",
                decision_node: "bg-yellow-100",
            }[type] || "bg-blue-100"

            return (
                <div className="bg-white border-2 rounded-lg p-4 cursor-pointer transition-all w-full h-full pointer-events-auto">
                    <div className="flex items-center justify-between mb-2">
                        <div className={`p-1 rounded ${bgClass}`}>
                            <LucideIcon name={iconName[type]} className="h-5 w-5 text-gray-700" />
                        </div>
                        <div className="flex gap-1">
                            <Button
                                size="sm" variant="ghost"
                                className={`h-6 w-6 p-0 ${connectionState?.isConnecting && connectionState?.fromNodeId !== element?.id
                                        ? "animate-pulse ring-2 ring-blue-400"
                                        : connectionState?.isConnecting && connectionState?.fromNodeId === element?.id
                                            ? "ring-2 ring-green-400" : ""
                                    }`}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (!connectionState?.isConnecting) startConnection(element)
                                    else if (connectionState?.fromNodeId !== element?.id) finishConnection(element)
                                    else cancelConnection()
                                }}
                            >
                                <Link className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500"
                                onClick={(e) => { e.stopPropagation(); handleDeleteSelectedElement() }}>
                                <LucideIcon name="X" className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                    <h4 className="font-medium text-sm mb-1">{element?.title || "無標題"}</h4>
                    <p className="text-xs text-muted-foreground line-clamp-2">{element?.description || "無描述"}</p>
                </div>
            )
        }

        // ── 流程連接線 ───────────────────────────────────────────────────────
        case "flow_line": {
            const x1 = element?.relP1?.x ?? 0, y1 = element?.relP1?.y ?? 0
            const x2 = element?.relP2?.x ?? (element?.width ?? 1)
            const y2 = element?.relP2?.y ?? (element?.height ?? 1)
            const color = "#3b82f6"
            const dx = x2 - x1, dy = y2 - y1
            const distance = Math.sqrt(dx * dx + dy * dy)
            const ctrl = Math.min(distance * 0.4, 150)
            const d = `M ${x1} ${y1} C ${x1 + ctrl} ${y1}, ${x2 - ctrl} ${y2}, ${x2} ${y2}`
            const markerId = `arrowhead-${element?.id}-${element?.toId}`

            return (
                <svg className="absolute pointer-events-none"
                    style={{ zIndex: 5, width: element?.width ?? 1, height: element?.height ?? 1, overflow: "visible" }}>
                    <defs>
                        <marker id={markerId} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto" markerUnits="userSpaceOnUse">
                            <polygon points="0 0, 10 3.5, 0 7" fill={color} strokeWidth={0.5} stroke="rgba(255,255,255,0.3)" />
                        </marker>
                    </defs>
                    <path d={d} stroke={color} strokeWidth={4} fill="none" opacity="0.15" strokeLinecap="round" />
                    <path d={d} stroke="rgba(0,0,0,0.1)" strokeWidth={3} fill="none" transform="translate(1,1)" strokeLinecap="round" />
                    <path d={d} stroke={color} strokeWidth={4} fill="none" markerEnd={`url(#${markerId})`} strokeLinecap="round" />
                    <path d={d} stroke="rgba(255,255,255,0.4)" strokeWidth={1} fill="none" strokeLinecap="round" />
                    <g className="pointer-events-auto">
                        <circle cx={(x1 + x2) / 2} cy={(y1 + y2) / 2} r={8} fill="white" stroke={color} strokeWidth={1.5}
                            className="cursor-pointer hover:fill-red-50 transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleDeleteSelectedElement() }} />
                        <text x={(x1 + x2) / 2} y={(y1 + y2) / 2}
                            textAnchor="middle" dominantBaseline="middle" fontSize={8} fill={color}
                            className="pointer-events-none select-none">×</text>
                    </g>
                </svg>
            )
        }

        // ── 直線 ─────────────────────────────────────────────────────────────
        case "straight-line": {
            const lineStyle = getLineStyle?.(element) || {}
            const x1 = element?.relP1?.x ?? 0, y1 = element?.relP1?.y ?? 0
            const x2 = element?.relP2?.x ?? (element?.width ?? 100)
            const y2 = element?.relP2?.y ?? (element?.height ?? 0)
            const rightId = `arr-right-${element?.id}`, leftId = `arr-left-${element?.id}`
            const markerEnd = element?.arrowType !== "left" && element?.arrowType !== "none" ? `url(#${rightId})` : undefined
            const markerStart = element?.arrowType === "both" || element?.arrowType === "left" ? `url(#${leftId})` : undefined

            return (
                <svg overflow="visible" width={element?.width} height={element?.height} className="pointer-events-none" style={{ display: "block" }}>
                    <defs>
                        <marker id={rightId} markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto">
                            <path d="M0,0 L10,5 L0,10 z" fill={lineStyle.stroke || "#374151"} />
                        </marker>
                        <marker id={leftId} markerWidth="10" markerHeight="10" refX="0" refY="5" orient="auto">
                            <path d="M10,0 L0,5 L10,10 z" fill={lineStyle.stroke || "#374151"} />
                        </marker>
                    </defs>
                    <line x1={x1} y1={y1} x2={x2} y2={y2} markerEnd={markerEnd} markerStart={markerStart} {...lineStyle} />
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth="10" className="cursor-pointer" style={{ pointerEvents: "all" }} />
                </svg>
            )
        }

        // ── 群組卡片 (card) ───────────────────────────────────────────────────
        case "card": {
            const variant = element?.variant || "default"
            const childElements = getCardChildren(element?.id) || []

            const variantStyles = {
                "blue-header": {
                    card: "w-full h-full flex flex-col rounded-lg overflow-hidden bg-white border border-sky-200 shadow-sm pointer-events-auto",
                    header: "px-4 py-3 bg-sky-50 border-b border-sky-200",
                    content: "p-4 flex-1 relative min-h-[120px]",
                },
                outline: {
                    card: "w-full h-full flex flex-col rounded-lg overflow-hidden bg-white border border-slate-300 pointer-events-auto",
                    header: "pb-3 px-4 pt-4",
                    content: "p-4 flex-1 relative min-h-[120px]",
                },
                elevated: {
                    card: "w-full h-full flex flex-col rounded-lg overflow-hidden bg-white border border-slate-200 shadow-lg pointer-events-auto",
                    header: "pb-3 px-4 pt-4",
                    content: "p-4 flex-1 relative min-h-[120px]",
                },
                muted: {
                    card: "w-full h-full flex flex-col rounded-lg overflow-hidden bg-slate-50 border border-slate-200 pointer-events-auto",
                    header: "pb-3 px-4 pt-4 bg-slate-100 border-b border-slate-200",
                    content: "p-4 flex-1 relative min-h-[120px]",
                },
                default: {
                    card: "w-full h-full flex flex-col rounded-lg overflow-hidden bg-white border border-border pointer-events-auto",
                    header: "pb-3 px-4 pt-4",
                    content: "p-4 flex-1 relative min-h-[120px]",
                },
            }
            const s = variantStyles[variant] || variantStyles.default

            return (
                <Card className={s.card}>
                    <CardHeader className={s.header}>
                        <CardTitle className="text-base">{element?.label || "群組標題"}</CardTitle>
                        {element?.placeholder && (
                            <CardDescription className="text-xs">{element.placeholder}</CardDescription>
                        )}
                    </CardHeader>
                    <CardContent
                        className={s.content}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
                        onDrop={(e) => {
                            e.preventDefault(); e.stopPropagation()
                            if (typeof onDropElement !== "function") return
                            let etd = null
                            try { const raw = e.dataTransfer.getData("application/json"); if (raw) etd = JSON.parse(raw) } catch { }
                            if (!etd) {
                                const t = e.dataTransfer.getData("component-type") || e.dataTransfer.getData("type")
                                const cid = e.dataTransfer.getData("component-id")
                                if (t) etd = { type: t, component_definitions_id: cid ? Number(cid) : undefined }
                            }
                            if (!etd) return
                            const rect = e.currentTarget.getBoundingClientRect()
                            const pad = typeof CARD_INNER_PADDING === "number" ? CARD_INNER_PADDING : 16
                            onDropElement(etd, { x: Math.max(0, e.clientX - rect.left - pad), y: Math.max(0, e.clientY - rect.top - pad) }, element?.id)
                        }}
                        onMouseMove={(e) => { handleMouseMove(e); handleResizeMouseMove(e) }}
                        onMouseUp={() => { handleMouseUp(); handleResizeMouseUp() }}
                        style={{
                            backgroundColor: selectedElement?.id === element?.id ? "#eff6ff" : "transparent",
                            border: selectedElement?.id === element?.id ? "2px dashed #3b82f6" : "2px dashed transparent",
                            borderRadius: 8,
                        }}
                    >
                        {childElements.length === 0 ? (
                            <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                                拖曳其他元件到此區域
                            </div>
                        ) : (
                            <div className="relative w-full h-full">
                                {childElements.map(child => {
                                    const cx = typeof child.x === "object"
                                        ? Number(child.x[canvasMode] ?? child.x.desktop ?? 0) : Number(child.x ?? child.xLegacy ?? 0)
                                    const cy = typeof child.y === "object"
                                        ? Number(child.y[canvasMode] ?? child.y.desktop ?? 0) : Number(child.y ?? child.yLegacy ?? 0)

                                    return (
                                        <div
                                            key={child.id}
                                            className={`absolute ${mode === "design" ? "border border-dashed border-slate-200 rounded p-1" : ""} ${selectedElement?.id === child.id ? "ring-2 ring-blue-400 rounded" : ""}`}
                                            style={{
                                                left: cx, top: cy,
                                                width: child?.style?.width || child?.width,
                                                height: child?.style?.height || child?.height,
                                                ...child?.style,
                                                pointerEvents: "none",
                                            }}
                                            onClick={() => { if (mode === "design") handleElementClick(child) }}
                                            onMouseDown={(e) => { if (mode === "design") handleMouseDown(e, child) }}
                                        >
                                            <RenderDisplayComponent
                                                {...{
                                                    element: child, selectedElement, selectedType, connectionMode,
                                                    connectionState, setSelectedElement, setPages, addChildNode,
                                                    handleDeleteSelectedElement, startConnection, finishConnection,
                                                    cancelConnection, createConnectedNode, canvasMode,
                                                    getPriorityColor, getPriorityText, getLineStyle, getEdgeCenterGeom,
                                                    computeLineGeom, handleElementClick, currentPageId, setConnectionMode,
                                                    currentPage, getCardChildren, onDropElement, CARD_INNER_PADDING,
                                                    mode, isEditing, setEditingElementId, handleMouseDown, setEditingContent,
                                                    RenderFillComponent, updateElementContent, editingContent,
                                                    handleFinishEditing, handleResizeMouseDown,
                                                    showBlueBorder: mode === "design" && selectedElement?.id === child.id,
                                                    handleMouseMove, handleResizeMouseMove, handleMouseUp, handleResizeMouseUp,
                                                    getPageCards, handleChangeContainer, getOrFetchItem, getItem,
                                                }}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )
        }
        // ── 按鈕（簡化版，僅展示樣式）────────────────────────────────────────────
        case 'button': {
            return (
                <div className="w-full space-y-1.5  pointer-events-auto">
                    <Button
                        variant={element?.buttonVariant || 'default'}
                        className={element?.isFullWidth ? "w-full" : ""}
                        size={element?.buttonSize}
                        disabled={true}>
                        {element?.label || element?.content  || '按鈕'}
                    </Button>

                </div>
            )
        }
        case "rectangle": {
            return (
                <div
                    className={`absolute cursor-pointer border-2 group pointer-events-auto ${selectedElement?.id === element?.id && selectedType === "element"
                        ? "border-green-500"
                        : connectionMode === "selecting-target"
                            // && connectionSource !== element?.id
                            ? "border-green-300 hover:border-green-500"
                            : connectionMode === "connecting"
                                ? "border-orange-300 hover:border-orange-500"
                                : "border-transparent hover:border-blue-300"
                        }`}
                    style={{
                        // left: element?.x,
                        // top: element?.y,
                        width: element?.width,
                        height: element?.height,
                        zIndex: 2,
                        ...element?.style,
                    }}
                    onClick={() => {
                        handleElementClick(element)
                    }}
                >
                    {element?.content}

                    {/* 選中狀態的調整手柄 */}
                    {/* {selectedElement?.id === element?.id && selectedType === "element" && connectionMode === "none" && (
                              <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full cursor-move"></div>
                          )} */}

                    {/* 連接模式的視覺提示 */}
                    {connectionMode === "connecting" && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation()

                                setPages(prev => {
                                    let newPages = prev.map((page, idx) => {

                                        if (page?.id === currentPageId) {
                                            setSelectedElement((pre) => {
                                                return {
                                                    ...pre,
                                                    x: element?.x,
                                                    y: element?.y,
                                                    width: element?.width,
                                                    height: element?.height,
                                                    fromId: element?.id
                                                }
                                            })
                                            return {
                                                ...page,
                                                elements: page?.elements?.map(el =>
                                                    el?.id === selectedElement?.id
                                                        ? {
                                                            ...el,
                                                            fromId: element?.id

                                                        }
                                                        : el
                                                )
                                            }
                                        }
                                        else {
                                            return page
                                        }
                                    })

                                    return newPages
                                });
                                setConnectionMode("selecting-target")
                            }}
                            className="absolute -top-8 left-1/2 transform -translate-x-1/2  px-1 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded text-xs shadow-lg transition-colors flex items-center gap-1"
                            title="選擇起始點"
                        >
                            起始
                        </button>

                    )}

                    {connectionMode === "selecting-target"
                        //  && connectionSource !== element?.id 
                        && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();

                                    const lineId = selectedElement?.id;

                                    setPages(prev => prev.map(page => {
                                        if (page.id !== currentPageId) return page;

                                        const line = page.elements.find(el => el?.id === lineId);
                                        const fromEl = page.elements.find(el => el?.id === (line?.fromId ?? selectedElement?.fromId));
                                        const toEl = element; // 當前點擊的矩形

                                        if (!fromEl || !toEl) return page;

                                        const { bbox, rel } = computeLineGeom(fromEl, toEl, 0, canvasMode);

                                        return {
                                            ...page,
                                            elements: page.elements.map(el =>
                                                el?.id === lineId
                                                    ? {
                                                        ...el,
                                                        // 關聯
                                                        fromId: fromEl?.id,
                                                        toId: toEl?.id,
                                                        // 外框位置與尺寸（容器 div 會用這些）
                                                        x: bbox.left,
                                                        y: bbox.top,
                                                        width: bbox.width,
                                                        height: bbox.height,
                                                        style: {
                                                            ...(el?.style || {}),
                                                            width: bbox.width,
                                                            height: bbox.height,
                                                        },
                                                        // 端點（相對於外框）供 SVG 繪線使用
                                                        relP1: rel?.p1,
                                                        relP2: rel?.p2,
                                                        // 預設線型與箭頭（可依你 UI 再改）
                                                        lineStyle: el?.lineStyle ?? "solid",
                                                        arrowType: el?.arrowType ?? "right",
                                                    }
                                                    : el
                                            )
                                        };
                                    }));

                                    // 可選：選中該線條（或保持原狀）
                                    setSelectedElement(prev => prev && prev.id === lineId ? { ...prev, toId: element?.id } : prev);
                                    setConnectionMode("none");
                                }}
                                className="absolute -top-8 left-1/2 transform -translate-x-1/2  px-1 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs shadow-lg transition-colors flex items-center gap-1"
                                title="選擇目標"
                            >
                                目標
                            </button>

                        )}

                    {/* 選中狀態時顯示的新增按鈕 */}
                    {selectedElement?.id === element?.id && selectedType === "element" && connectionMode === "none" && (
                        <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 flex gap-1">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    addChildNode(element?.id, "same-level-left", element?.component_definitions_id)
                                }}
                                className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs shadow-lg transition-colors flex items-center gap-1"
                                title="新增關聯節點"
                            >
                                +左

                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    addChildNode(element?.id, "same-level-right", element?.component_definitions_id)
                                }}
                                className="px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs shadow-lg transition-colors flex items-center gap-1"
                                title="新增關聯節點"
                            >
                                +右
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    addChildNode(element?.id, "top", element?.component_definitions_id)
                                }}
                                className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs shadow-lg transition-colors flex items-center gap-1"
                                title="新增關聯節點"
                            >
                                +上
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    addChildNode(element?.id, "bottom", element?.component_definitions_id)
                                }}
                                className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-xs shadow-lg transition-colors flex items-center gap-1"
                                title="新增關聯節點"
                            >
                                +下
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleDeleteSelectedElement()
                                }}
                                className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs shadow-lg transition-colors flex items-center gap-1"
                                title="刪除"
                            >
                                刪除
                            </button>
                        </div>
                    )}
                </div>)

        }
        case "straight-line": {
            const editPageElement = currentPage?.elements || [];
            const fromElement = editPageElement.find(ele => ele?.id === element?.fromId);
            const toElement = editPageElement.find(ele => ele?.id === element?.toId);
            if (!fromElement || !toElement) return null;

            const lineStyle = getLineStyle(element, selectedElement);
            const x1 = element?.relP1?.x ?? 0;
            const y1 = element?.relP1?.y ?? 0;
            const x2 = element?.relP2?.x ?? element?.width;
            const y2 = element?.relP2?.y ?? element?.height;

            const rightId = `arrowhead-right-${element?.id}`;
            const leftId = `arrowhead-left-${element?.id}`;
            let arrowType = element?.style?.arrowType || "none"
            const markerEnd = (arrowType === "right" || arrowType === "both") ? `url(#${rightId})` : undefined;
            const markerStart = (arrowType === "left" || arrowType === "both") ? `url(#${leftId})` : undefined;

            return (
                <svg overflow="visible" width={element?.width} height={element?.height} className="pointer-events-none" style={{ display: "block" }}>
                    <defs>
                        <marker id={rightId} markerWidth="10" markerHeight="10" refX="10" refY="5" orient="auto">
                            <path d="M0,0 L10,5 L0,10 z" fill={lineStyle.stroke || "#374151"} />
                        </marker>
                        <marker id={leftId} markerWidth="10" markerHeight="10" refX="0" refY="5" orient="auto">
                            <path d="M10,0 L0,5 L10,10 z" fill={lineStyle.stroke || "#374151"} />
                        </marker>
                    </defs>

                    <line x1={x1} y1={y1} x2={x2} y2={y2} markerEnd={markerEnd} markerStart={markerStart} {...lineStyle} />
                    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth="10" className="cursor-pointer" style={{ pointerEvents: "all" }} />
                </svg>
            );
        }


        // ── Fallback ──────────────────────────────────────────────────────────
        default:
            return (
                <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 pointer-events-auto border border-dashed border-gray-200 rounded">
                    {type || "未知元件"}
                </div>
            )
    }
}

export default RenderDisplayComponent
